/**
 * Séances académiques — cours, examens, révisions, soutien.
 *
 * Ces types reflètent `AppointmentDto` du back tel qu'il est réellement sérialisé :
 * la date et l'heure y sont deux champs distincts (`scheduledDate` en date pure,
 * `startTime`/`endTime` en `TimeSpan` → "HH:mm:ss"). Utiliser `sessionStart()` /
 * `sessionEnd()` pour obtenir un instant exploitable par le calendrier.
 */

/** Nature d'une séance — valeurs de l'enum `AcademicSessionKind` côté back. */
export type SessionKind = 'Course' | 'Exam' | 'Revision' | 'Tutoring' | 'Other';

/** Valeurs numériques attendues par l'API à la création/modification. */
export const SESSION_KIND_VALUE: Record<SessionKind, number> = {
  Course: 1,
  Exam: 2,
  Revision: 3,
  Tutoring: 4,
  Other: 9,
};

export const SESSION_KINDS: SessionKind[] = ['Course', 'Exam', 'Revision', 'Tutoring', 'Other'];

/** Statuts de l'enum `AppointmentStatus`. */
export type AppointmentStatus =
  | 'Scheduled'
  | 'Confirmed'
  | 'InProgress'
  | 'Completed'
  | 'Cancelled'
  | 'NoShow';

export interface AppointmentAttendee {
  studentId: number;
  fullName: string;
}

/** Support produit par une séance — la vidéo du cours, le plus souvent. */
export interface AppointmentRecording {
  id: number;
  title: string;
  fileName?: string | null;
  category: string;
  fileSizeBytes?: number | null;
  contentType?: string | null;
  createdAt: string;
  /** URL présignée de lecture directe. Null pour un lien externe. */
  previewUrl?: string | null;
  externalUrl?: string | null;

  /**
   * Le support est archivé.
   *
   * <p>Les archivés étaient écartés côté serveur : le professeur qui rouvrait
   * sa séance ne voyait plus la vidéo qu'il y avait rattachée et la croyait
   * perdue. Ils sont rendus, à charge pour l'écran de les distinguer.</p>
   */
  isArchived?: boolean;

  /** Le support est visible des élèves. Faux tant qu'il n'a pas été diffusé. */
  isPublic?: boolean;
}

export interface AcademicAppointment {
  id: number;
  title: string;
  /** Notes / consignes de la séance (champ `reason` côté back). */
  reason?: string | null;

  /** Date pure, ISO ("2026-09-14T00:00:00"). L'heure y est toujours nulle. */
  scheduledDate: string;
  /** "HH:mm:ss". */
  startTime: string;
  /** "HH:mm:ss". */
  endTime: string;
  durationMinutes: number;

  /** Matière principale — celle qui donne sa couleur à la séance. */
  subjectId?: number | null;
  /** Libellé historique, repli quand une traduction manque. */
  subjectName?: string | null;
  subjectNameAr?: string | null;
  subjectNameFr?: string | null;
  subjectNameEn?: string | null;

  /**
   * Toutes les matières traitées, la principale en tête.
   *
   * Un même cours sert souvent plusieurs niveaux, qui sont autant de matières
   * distinctes au référentiel. Les champs `subject*` ci-dessus restent ceux de
   * la principale : un écran qui n'en affiche qu'une n'a pas à connaître cette
   * liste.
   */
  subjects?: SchedulingSubject[];

  groupId?: number | null;
  groupName?: string | null;

  /** Élèves conviés nommément. Vide dans la vue élève. */
  students: AppointmentAttendee[];
  attendeeCount: number;

  consultantProfessorId?: number | null;
  consultantName?: string | null;

  type: string;
  sessionKind?: SessionKind | null;
  status: AppointmentStatus;

  /** Au moins un rapport d'inspection déposé sur la séance. */
  isInspected?: boolean;

  location?: string | null;
  meetingUrl?: string | null;
  meetingProvider?: string | null;
  meetingPassword?: string | null;

  createdAt?: string | null;
  updatedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;

  /** Notes du professeur — porte le motif quand une séance n'a pas eu lieu. */
  consultantNotes?: string | null;

  /**
   * Séance sans destinataire : ni groupe, ni élève convié. C'est une séance de
   * production — le professeur bloque un créneau pour enregistrer un cours.
   * Elle n'apparaît dans aucun agenda d'élève, et c'est lui qui en déclare
   * l'issue, faute de journal d'accès qui puisse l'attester.
   */
  isProduction?: boolean;

  /** Issue déclarée par le professeur. `null` tant qu'il ne l'a pas fait. */
  held?: boolean | null;

  /** Supports produits par la séance. */
  recordings?: AppointmentRecording[];
}

export interface CreateAcademicAppointmentDto {
  /** Matière principale. Toujours la première de `subjectIds`. */
  subjectId: number;
  /** Toutes les matières traitées — un même cours peut servir plusieurs niveaux. */
  subjectIds?: number[];
  groupId?: number | null;
  studentIds: number[];
  sessionKind: number;
  title: string;
  reason?: string | null;
  /** "YYYY-MM-DD". */
  scheduledDate: string;
  /** "HH:mm:ss". */
  startTime: string;
  durationMinutes: number;
  location?: string | null;
  meetingUrl?: string | null;
  meetingProvider?: string | null;
  meetingPassword?: string | null;
  /** Réservé à l'admin : le prof connecté est animateur d'office. */
  consultantProfessorId?: number | null;
}

export interface UpdateAcademicAppointmentDto {
  title?: string | null;
  reason?: string | null;
  subjectId?: number | null;
  /**
   * Remplace l'ensemble des matières. Omis, elles restent inchangées ; envoyer
   * une liste vide est refusé — une séance porte au moins une matière.
   */
  subjectIds?: number[];
  sessionKind?: number | null;
  groupId?: number | null;
  /** Retire le groupe sans en désigner un autre. */
  clearGroup?: boolean;
  /** Remplace la liste des élèves. Omettre pour ne pas y toucher. */
  studentIds?: number[];
  scheduledDate?: string | null;
  startTime?: string | null;
  durationMinutes?: number | null;
  location?: string | null;
  meetingUrl?: string | null;
  meetingProvider?: string | null;
  meetingPassword?: string | null;
}

/** Filtres du calendrier. Un filtre omis ne restreint rien. */
export interface AcademicCalendarFilters {
  from?: string;
  to?: string;
  subjectId?: number | null;
  groupId?: number | null;
  studentId?: number | null;
  includeCancelled?: boolean;
}

// Référentiel de planification (GET .../options)

export interface SchedulingSubject {
  id: number;
  /** Libellé historique, repli quand une traduction manque. */
  name: string;
  nameAr?: string | null;
  nameFr?: string | null;
  nameEn?: string | null;
  learningPathId: number;

  /**
   * Titre du parcours dont relève la matière.
   *
   * Un même nom de matière existe dans plusieurs parcours — l'anglais est
   * enseigné en 6ème primaire, en 9ème de base et au baccalauréat. Sans le
   * parcours, la liste affiche trois options rigoureusement identiques.
   */
  learningPathTitle?: string | null;
  learningPathTitleAr?: string | null;
  learningPathTitleFr?: string | null;
  learningPathTitleEn?: string | null;
}

export interface SchedulingGroup {
  id: number;
  name: string;
  learningPathId: number;
  memberCount: number;
}

export interface SchedulingStudent {
  id: number;
  fullName: string;
  learningPathId: number;
  /** Groupes du professeur auxquels l'élève appartient. */
  groupIds: number[];
  /** Matières du professeur suivies par l'élève. */
  subjectIds: number[];
}

export interface SchedulingProfessor {
  id: number;
  fullName: string;
}

export interface AcademicSchedulingOptions {
  subjects: SchedulingSubject[];
  groups: SchedulingGroup[];
  students: SchedulingStudent[];
  /** Alimenté seulement côté administration. */
  professors: SchedulingProfessor[];
}

// Helpers de conversion date/heure

/** Partie date seule ("2026-09-14") d'une `scheduledDate` renvoyée par l'API. */
export function sessionDay(a: SessionTiming): string {
  return (a.scheduledDate ?? '').slice(0, 10);
}

/** Instant de début, combinant `scheduledDate` et `startTime`. */
/** Ce qu'il faut d'une séance pour la placer dans le temps — l'administration et l'inspection le fournissent toutes deux. */
export interface SessionTiming {
  scheduledDate: string;
  startTime: string;
  endTime?: string | null;
  durationMinutes?: number | null;
}

export function sessionStart(a: SessionTiming): string {
  return `${sessionDay(a)}T${normalizeTime(a.startTime)}`;
}

/**
 * Instant de fin. Retombe sur la durée si `endTime` manque.
 *
 * <p>Le repli renvoyait l'heure de <i>début</i> : la séance devenait un
 * événement de durée nulle, réduit à un filet de quelques pixels dans la
 * grille horaire, que le calendrier ne savait plus placer face à ses
 * voisines. Le commentaire promettait déjà le bon comportement — c'est le
 * code qui manquait.</p>
 *
 * <p>Dernier recours si ni l'heure de fin ni la durée ne sont connues : une
 * heure. Mieux vaut une séance de durée plausible qu'une séance invisible.</p>
 */
export function sessionEnd(a: SessionTiming): string {
  if (a.endTime) return `${sessionDay(a)}T${normalizeTime(a.endTime)}`;

  const minutes = a.durationMinutes && a.durationMinutes > 0 ? a.durationMinutes : 60;
  return `${sessionDay(a)}T${addMinutes(a.startTime, minutes)}`;
}

/** "HH:mm[:ss]" + n minutes, borné à la journée. */
function addMinutes(time: string | null | undefined, minutes: number): string {
  const [h, m] = normalizeTime(time).split(':').map(Number);
  const total = Math.min(h * 60 + m + minutes, 23 * 60 + 59);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}:00`;
}

/** "HH:mm" pour l'affichage, depuis un "HH:mm:ss" du back. */
export function displayTime(time: string | null | undefined): string {
  return (time ?? '').slice(0, 5);
}

/** Le back accepte "HH:mm:ss" ; le navigateur produit "HH:mm". */
function normalizeTime(time: string | null | undefined): string {
  const t = time ?? '00:00:00';
  return t.length === 5 ? `${t}:00` : t;
}

/** Cible lisible d'une séance : le groupe, sinon les élèves conviés. */
export function sessionTarget(a: AcademicAppointment): string {
  if (a.groupName) return a.groupName;
  if (a.students?.length === 1) return a.students[0].fullName;
  if (a.students?.length > 1) return `${a.students.length}`;
  return '';
}
